import React from 'react';
import { Lock as LockIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Box, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  DraftChapter,
  DraftPolicyItem,
  DraftPrefaceItem,
  SidebarItem,
} from 'views/App/Portal/Policy/_types/manuals/draft';
import HoverPreview from './HoverPreview';
import { useManualStructureContext } from '../context';

interface DraggableItemProps {
  item: DraftPrefaceItem | DraftPolicyItem | DraftChapter;
  title: string;
  itemType: 'preface' | 'chapter' | 'policy';
  isSelected?: boolean;
  handleToggleState: (type: 'preface' | 'chapter' | 'policy', id: string, event: React.MouseEvent) => void;
  handleChapterClick?: (id: string) => void;
  sidebarItem?: SidebarItem | null;
  setSidebarItem?: (item: SidebarItem | null) => void;
}
const DraggableItem = ({
  item,
  title,
  itemType,
  isSelected = false,
  handleToggleState,
  handleChapterClick,
  sidebarItem,
  setSidebarItem,
}: DraggableItemProps) => {
  const { hoveredItem, setHoveredItem, setAnchorEl, setPopoverPlacement, reorderedManual, handleDelete, wasAlreadyDisabled } =
    useManualStructureContext();

  const id = item.id;
  const isDisabled = item.state === 'DISABLED';
  const isLocked = itemType !== 'chapter' && (item as DraftPrefaceItem | DraftPolicyItem).isLocked;
  const lockedUser = itemType !== 'chapter' ? (item as DraftPrefaceItem | DraftPolicyItem).lock?.username : '';
  const isDeleted = (item as any).isDeleted === true;

  const isAgencyManual = reorderedManual?.manualLevel === 'AGENCY';
  const wasDisabled = wasAlreadyDisabled(itemType, id);
  // Show delete when already disabled (saved state); allow server to enforce agency/unissued on actual delete
  const showDeleteIcon = isAgencyManual && isDisabled && wasDisabled;

  const handleClick = () => {
    if (itemType === 'chapter' && handleChapterClick) {
      handleChapterClick(id);
      return;
    }

    if (setSidebarItem)
      id === sidebarItem?.item.id
        ? setSidebarItem(null)
        : setSidebarItem({ item: item as DraftPrefaceItem | DraftPolicyItem, type: itemType as 'preface' | 'policy' });
  };

  const handleHoveredItem = (event: React.MouseEvent<HTMLElement>) => {
    if (itemType !== 'chapter') {
      const hoveredElement = event.currentTarget;
      const rect = hoveredElement.getBoundingClientRect();

      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;

      if (spaceBelow < 250 && spaceAbove > 250) {
        setPopoverPlacement('top');
      } else {
        setPopoverPlacement('bottom');
      }

      setHoveredItem(item as DraftPrefaceItem | DraftPolicyItem);
      setAnchorEl(event.currentTarget);
    }
  };
  return (
    <Box
      onMouseEnter={(event) => handleHoveredItem(event)}
      onMouseLeave={() => {
        setHoveredItem(null);
        setAnchorEl(null);
      }}
      sx={{ position: 'relative' }}
    >
      <Paper
        sx={{
          p: 2,
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderRadius: '8px',
          border: isSelected ? '2px solid #1976d2' : '1px solid #ddd',
          backgroundColor: isSelected ? '#bbdefb' : isDisabled ? '#f0f0f0' : 'white',
          color: isDisabled ? '#b4b4b4' : '#000',
          cursor: 'pointer',
          opacity: isDeleted ? 0.6 : 1,
        }}
        onClick={() => handleClick()}
      >
        <Typography
          sx={{
            textDecoration: isDeleted ? 'line-through' : 'none',
          }}
        >
          {title}
        </Typography>
        {isLocked && (
          <Tooltip title={lockedUser}>
            <LockIcon
              color='action'
              sx={{ marginLeft: 'auto', cursor: 'pointer' }}
            />
          </Tooltip>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={(e) => handleToggleState(itemType, id, e)}>
            {!isDisabled ? <VisibilityIcon /> : <VisibilityOffIcon />}
          </IconButton>
          {showDeleteIcon && (
            <IconButton
              size='small'
              color='error'
              onClick={(e) => handleDelete(itemType as 'chapter' | 'policy', id, e)}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      </Paper>
      {hoveredItem?.id === item.id && (
        <Box
          sx={{
            zIndex: 10,
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
            borderRadius: '12px',
          }}
        >
          <HoverPreview itemType={itemType as 'preface' | 'policy'} />
        </Box>
      )}
    </Box>
  );
};

export default DraggableItem;
